import { Box, Flex, Stack } from '@chakra-ui/react';
import Input from '@components/atoms/Input';
import { useGetReferralUser } from '@components/crm/shared/queries';
import Button from '@components/interactive/Button';
import DefaultLayout from '@components/layout/layout/DefaultLayout';
import Body1 from '@components/typography/Body1';
import Header from '@components/typography/Header';
import { submitReferralEmail } from '@lib/requests';
import { useMutation } from '@tanstack/react-query';
import { FsmColors } from 'chakra-ui/fsmTheme';
import { AnimatePresence, motion } from 'framer-motion';
import { useRouter } from 'next/router';
import { useState } from 'react';
import Confetti from 'react-confetti';
import { BsChevronLeft, BsMailbox, BsSend } from 'react-icons/bs';
import { titleCase } from 'title-case';

const ReferFriend = () => {
  const router = useRouter();
  const { userId, source } = router.query;

  const { data: userData } = useGetReferralUser({ userId });

  const submitEmail = useMutation({
    mutationFn: submitReferralEmail,
  });

  const [currentStep, setCurrentStep] = useState('initial'); // 'initial', 'email-form', or 'address-form'
  const [emailFormData, setEmailFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });

  if (Object.keys(userData).length === 0) {
    return null;
  }

  const agentName = `${userData?.lead_owner?.first_name} ${userData?.lead_owner?.last_name}`;

  const handleEmailClick = () => {
    setCurrentStep('email-form');
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  };

  const handleBack = () => {
    setCurrentStep('initial');
    setEmailFormData({
      firstName: '',
      lastName: '',
      email: '',
    });
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  };

  const handleEmailInputChange = (e) => {
    setEmailFormData({
      ...emailFormData,
      [e.target.name]: e.target.value,
    });
  };

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleSendEmail = () => {
    const to = encodeURIComponent(emailFormData.email.trim());
    const firstName = titleCase(emailFormData.firstName.trim());
    const clientName = titleCase(`${userData.first_name.toLowerCase()} ${userData.last_name.toLowerCase()}`);
    const calendlyLink = `https://calendly.com/${userData.lead_owner?.agent_profile?.scheduling_system_slug}/welcome-to-medicare`;
    const agentFirstName = titleCase(userData.lead_owner.first_name.toLowerCase());
    const directLine = userData.direct_line || '';
    submitEmail.mutate({ userId, source, ...emailFormData });

    const body = encodeURIComponent(
      `Hi ${firstName},
I wanted to pass along the contact info for ${agentName} who just helped me figure out Medicare. They're great.

If you'd like to chat with ${agentFirstName}: ${calendlyLink}.
You can also call ${agentFirstName} any time at ${directLine}.

Best,
${clientName}`
    );

    const cc = encodeURIComponent(userData?.lead_owner?.email);
    const mailto = `mailto:${to}?subject=Medicare 101 Invitation&body=${body}&cc=${cc}`;
    window.location.href = mailto;
    setCurrentStep('success');
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  };

  const backButton = (title) => (
    <Flex
      alignItems="start"
      justifyContent={['start', 'start', 'center']}
      position="relative"
      flexDirection={['column', 'row']}
    >
      <Button
        variant="secondary"
        onClick={handleBack}
        leftIcon={<BsChevronLeft size={20} />}
        size={['xs', 'sm']}
        position={['static', 'absolute']} // Remove absolute positioning on mobile
        left={[0, '0']} // Only apply left positioning on larger screens
      >
        Back
      </Button>
      <Header as="h3">{title}</Header>
    </Flex>
  );

  return (
    <DefaultLayout>
      <Box w="100%" maxW="1000px" mx="auto" mt={150} mb={150} p={6} maxH="800px">
        <Stack spacing={6}>
          <Header as="h2" textAlign="center">
            {`Thank You for Working with ${agentName}`}{' '}
          </Header>
          <AnimatePresence mode="wait">
            {currentStep === 'initial' && (
              <motion.div
                key="initial"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <Box border="2px solid" borderColor="gray.200" borderRadius="lg" p={8} bg="white">
                  <Header as="h4" textAlign="center">
                    {' '}
                    {`Have a friend or family member who could use ${userData.lead_owner.first_name}'s help? `}
                  </Header>
                  <Flex direction={['column', 'column', 'row']} alignItems="stretch" justifyContent="center">
                    <Box flex="1" textAlign="center" p={6}>
                      <Stack spacing={4} alignItems="center">
                        <BsMailbox size={40} color={FsmColors.blue} />
                        <Body1 fontWeight="medium">Send an Introduction Email</Body1>
                        <Button onClick={handleEmailClick} color="white" size="lg" w="100%">
                          Introduce Us
                        </Button>
                      </Stack>
                    </Box>
                  </Flex>
                </Box>
              </motion.div>
            )}

            {/* Email Form State */}
            {currentStep === 'email-form' && (
              <motion.div
                key="email-form"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <Box border="2px solid" borderColor="gray.200" borderRadius="lg" p={8} bg="white">
                  <Stack spacing={6}>
                    {backButton('Send an Introduction Email')}
                    <Body1 textAlign="center">{`We'll create an introduction email for you to send to a friend or family member.`}</Body1>
                    <Flex justifyContent="center">
                      <Box maxW="400px" w="100%">
                        <Stack spacing={4}>
                          <Box>
                            <Body1 textAlign="left" fontWeight="medium">
                              First Name
                            </Body1>
                            <Input
                              name="firstName"
                              value={emailFormData.firstName}
                              onChange={handleEmailInputChange}
                              w="100%"
                              border="1px solid"
                              placeholder="Enter first name"
                              required
                            />
                          </Box>

                          <Box>
                            <Body1 textAlign="left" fontWeight="medium">
                              Last Name
                            </Body1>
                            <Input
                              type="text"
                              name="lastName"
                              value={emailFormData.lastName}
                              onChange={handleEmailInputChange}
                              w="100%"
                              px={3}
                              py={3}
                              border="1px solid"
                              borderColor="gray.300"
                              borderRadius="md"
                              placeholder="Enter last name"
                              required
                            />
                          </Box>

                          <Box>
                            <Body1 textAlign="left" fontWeight="medium">
                              Email Address
                            </Body1>
                            <Input
                              name="email"
                              value={emailFormData.email}
                              onChange={handleEmailInputChange}
                              w="100%"
                              px={3}
                              py={3}
                              border="1px solid"
                              borderColor="gray.300"
                              borderRadius="md"
                              placeholder="Enter email address"
                              required
                            />
                          </Box>
                        </Stack>
                        <Stack>
                          <Button
                            onClick={handleSendEmail}
                            color="white"
                            size="lg"
                            w="100%"
                            mt={6}
                            leftIcon={<BsSend size={20} />}
                            isDisabled={
                              !emailFormData.firstName ||
                              !emailFormData.lastName ||
                              !emailFormData.email ||
                              !emailFormData.email.includes('@') ||
                              emailRegex.test(emailFormData.email) === false
                            }
                          >
                            Generate Email
                          </Button>
                        </Stack>
                      </Box>
                    </Flex>
                  </Stack>
                </Box>
              </motion.div>
            )}
            {currentStep === 'success' && (
              <motion.div
                key="success"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <Box border="2px solid" borderColor="gray.200" borderRadius="lg" p={8} bg="white">
                  <Stack spacing={6} alignItems="center">
                    <Confetti numberOfPieces={50} recycle={false} colors={['#9DBB91']} />
                    <Header as="h3">We Appreciate You Spreading The Word!</Header>
                    <Body1>Have someone else that we can help?</Body1>
                    <Button
                      onClick={() => {
                        handleBack();
                      }}
                    >
                      Refer Another
                    </Button>
                  </Stack>
                </Box>
              </motion.div>
            )}
          </AnimatePresence>
        </Stack>
      </Box>
    </DefaultLayout>
  );
};

export default ReferFriend;
